'use client';

import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { ChevronRight } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';
import { isValidLocale } from '@/lib/i18n/config';

const formatTitle = (path: string, t: (key: string) => string): string => {
  // Handle special cases with translations
  const translationKey = `breadcrumbs.${path}`;
  const translated = t(translationKey);
  
  // If translation exists and is not the same as the key, use it
  if (translated && translated !== translationKey) {
    return translated;
  }

  // Fallback: capitalize first letter and replace dashes with spaces
  return path.split('-').map(word =>
    word.charAt(0).toUpperCase() + word.slice(1)
  ).join(' ');
};

/** Breadcrumbs — designed for dark hero section backgrounds only. */
export default function Breadcrumbs() {
  const pathname = usePathname();
  const { t } = useTranslation('common');
  const paths = pathname.split('/').filter(Boolean);

  // Filter out locale from paths (first element is usually locale)
  const hasLocalePrefix = paths.length > 0 && isValidLocale(paths[0]);
  const filteredPaths = hasLocalePrefix ? paths.slice(1) : paths;
  const localePrefix = hasLocalePrefix ? `/${paths[0]}` : '';

  return (
    <nav aria-label="breadcrumb" className="flex items-center space-x-2 text-sm text-white/60 py-2">
      <Link href={`${localePrefix}/`} className="hover:text-gold transition-colors">
        {t('breadcrumbs.home')}
      </Link>
      {filteredPaths.map((path, index) => {
        const href = `/${paths.slice(0, index + (paths.length - filteredPaths.length) + 1).join('/')}`;
        const isLast = index === filteredPaths.length - 1;
        const title = formatTitle(path, t);

        return (
          <div key={path} className="flex items-center space-x-2">
            <ChevronRight className="h-4 w-4 text-gold" />
            {isLast ? (
              <span className="text-white font-medium" aria-current="page">{title}</span>
            ) : (
              <Link href={href} className="hover:text-gold transition-colors">
                {title}
              </Link>
            )}
          </div>
        );
      })}
    </nav>
  );
}
